import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, KpiSub, KpiValue, Trend } from '../../performance-operations/styles';
import { fetchMetrics, hasValue, metaOfMetrics, usePanel } from '../../performance-operations/config';
import {
  diffTone, formatNum, formatPct, formatScore, isSuppressed, metricByCode, resultDiff,
} from '../config';

// PM analitik P2 — sonuç KPI kartları (E01: 6 kart, E03: 4 kart). Değerler mevcut metrik ucundan (E01 ile E03 aynı uçtan aynı
// kodları okur → M06 için aynı değer ve n). Her sonuç kartı n ve kapsama oranını gösterir; final yokken "Veri Yok" (sıfır DEĞİL),
// küçük grupta "Gizli". Fark karşılaştırma dönemine göre: yüzdelerde yüzde puan, puanda puan farkı (yalnız aynı ölçek),
// kapsamda göreli yüzde. Renk her zaman ok ikonu ve sayıyla birlikte.
function Delta({ code, diff, comparePeriod }) {
  const { t, i18n } = useTranslation();
  if (!diff) return null;
  if (diff.reason) {
    return (
      <Trend $tone="neutral" data-trend="reason">
        {t(`performance.results.analytics.kpi.reason.${diff.reason}`)}
      </Trend>
    );
  }
  const tone = diffTone(code, diff.delta);
  const Icon = diff.delta > 0 ? ArrowUp : diff.delta < 0 ? ArrowDown : Minus;
  const sign = diff.delta > 0 ? '+' : '';
  const unit = diff.relative ? '%' : (code === 'M06' || code === 'M09' ? '' : ` ${t('performance.results.analytics.kpi.points')}`);
  return (
    <Trend $tone={tone} data-trend={tone}>
      <Icon size={14} aria-hidden="true" />
      <span>{`${sign}${formatNum(diff.delta, i18n.language)}${unit} ${t('performance.results.analytics.kpi.vsPeriod', { name: comparePeriod?.name || '' })}`}</span>
    </Trend>
  );
}

function valueText(t, code, m, lang) {
  if (!m) return t('performance.results.analytics.noData');
  if (isSuppressed(m)) return t('performance.results.analytics.suppressed');
  if (!hasValue(m)) return t('performance.results.analytics.noData');
  if (code === 'M01') return formatNum(m.value, lang, 0);
  if (code === 'M06') return formatScore(m.value, m.scaleMax, lang);
  if (code === 'M09') return formatScore(m.value, 100, lang);
  return formatPct(m.value, lang);
}

function subText(t, code, m, lang) {
  if (!m) return null;
  if (isSuppressed(m)) return t('performance.results.analytics.kpi.suppressedSub');
  const n = (v) => formatNum(v, lang, 0);
  switch (code) {
    case 'M01': return t('performance.results.analytics.kpi.employees');
    case 'M02': return hasValue(m) ? t('performance.results.analytics.kpi.ratio', { num: n(m.numerator), den: n(m.denominator) }) : null;
    case 'M07':
    case 'M08':
      return hasValue(m) ? t('performance.results.analytics.kpi.peopleOfN', { count: n(m.numerator), n: n(m.denominator) }) : null;
    default:
      // Sonuç kartı: hesaplanan n, uygun toplam ve kapsama (analiz §25 kart standardı).
      return t('performance.results.analytics.kpi.coverageSub', {
        n: n(m.sampleN ?? 0), total: n(m.eligibleN ?? 0), coverage: m.coverage === null || m.coverage === undefined ? '-' : formatNum(m.coverage, lang),
      });
  }
}

export default function ResultKpiCards({
  filters, codes, comparePeriod, onMeta, span = 3, kpi = false, hrefOf, onSelect,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const periodId = filters.periodId;
  const filterKey = JSON.stringify(filters);
  const codeKey = codes.join(',');
  const current = usePanel(() => fetchMetrics(filters, periodId, codes), [filterKey, codeKey], !!periodId);
  const previous = usePanel(() => fetchMetrics(filters, comparePeriod.id, codes), [filterKey, codeKey, comparePeriod?.id], !!periodId && !!comparePeriod);

  useEffect(() => {
    if (current.data) onMeta?.('kpi', metaOfMetrics(current.data));
  }, [current.data, onMeta]);

  return (
    <>
      {codes.map((code) => {
        const m = metricByCode(current.data, code);
        const p = metricByCode(previous.data, code);
        const diff = current.data && !previous.loading ? (comparePeriod ? (previous.error ? { reason: 'noData' } : resultDiff(code, m, p)) : { reason: 'noComparePeriod' }) : null;
        const href = hrefOf ? hrefOf(code, m) : null;
        const text = valueText(t, code, m, lang);
        let value = <KpiValue data-kpi={code}>{text}</KpiValue>;
        if (href) value = <Link to={href} data-link={`kpi-${code}`} aria-label={t(`performance.results.analytics.kpi.${code}`)}>{value}</Link>;
        else if (onSelect && hasValue(m) && !isSuppressed(m)) {
          value = (
            <Clickable type="button" onClick={() => onSelect(code)} data-link={`kpi-${code}`} aria-label={t(`performance.results.analytics.kpi.${code}`)}>
              {value}
            </Clickable>
          );
        }
        return (
          <PanelCard
            key={code} panelKey={`kpi-${code}`} title={t(`performance.results.analytics.kpi.${code}`)} span={span} kpi={kpi} compact
            loading={current.loading} error={current.error} onRetry={current.retry}
          >
            {value}
            <KpiSub data-sub={code}>{subText(t, code, m, lang)}</KpiSub>
            <Delta code={code} diff={diff} comparePeriod={comparePeriod} />
          </PanelCard>
        );
      })}
    </>
  );
}
